import styled, { css, keyframes } from 'styled-components';

// --- Container and Layout ---
export const Container = styled.div`
  padding: 32px 0 80px 0;
  max-width: 900px;
  margin: 0 auto;
`;

export const SectionCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 18px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding: 0;
  overflow: hidden;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  cursor: pointer;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

export const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #22223b;
`;

export const SectionSummary = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-left: 16px;
`;

export const CollapseIcon = styled.span`
  display: inline-block;
  transition: transform 0.2s;
  ${({ open }) => open && css`transform: rotate(90deg);`}
`;

export const SectionBody = styled.div`
  padding: 24px 28px 18px 48px;
  background: #fff;
`;

export const NestedIndent = styled.div`
  margin-left: 32px;
`;

// --- UI Elements ---
export const Chip = styled.span`
  display: inline-block;
  background: #f1f5f9;
  color: #334155;
  border-radius: 12px;
  font-size: 12px;
  padding: 2px 10px;
  margin-right: 8px;
`;

export const VariableChip = styled.button`
  display: inline-block;
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #93c5fd;
  border-radius: 6px;
  font-size: 11px;
  padding: 4px 8px;
  cursor: pointer;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  transition: all 0.2s ease;
  
  &:hover {
    background: #bfdbfe;
    border-color: #60a5fa;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export const FloatingButton = styled.button`
  position: fixed;
  right: 36px;
  bottom: 36px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  font-size: 32px;
  box-shadow: 0 4px 16px rgba(37,99,235,0.12);
  cursor: pointer;
  z-index: 100;
  transition: background 0.2s;
  &:hover { background: #1d4ed8; }
`;

// --- Modal Components ---
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(30,41,59,0.18);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ModalCard = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(30,41,59,0.18);
  padding: 40px 48px 32px 48px;
  min-width: 500px;
  max-width: 90vw;
  max-height: 85vh;
  overflow-y: auto;
`;

export const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 18px 0;
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
`;

// --- Form Elements ---
export const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 10px;
`;

export const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 10px;
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  margin-bottom: 10px;
`;

export const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  margin-bottom: 4px;
  display: block;
`;

export const FormGroup = styled.div`
  margin-bottom: 16px;
`;

// --- Buttons ---
export const Button = styled.button`
  background: #2563eb;
  color: #fff;
  border: none;
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #1d4ed8; }
  &:disabled { background: #cbd5e1; color: #64748b; cursor: not-allowed; }
`;

export const SecondaryButton = styled(Button)`
  background: #64748b;
  &:hover { background: #475569; }
`;

export const RemoveButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  margin-left: 8px;
  &:hover { background: #dc2626; }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

// --- Feedback and Messages ---
export const ErrorMsg = styled.div`
  color: #dc2626;
  font-size: 13px;
  margin-bottom: 8px;
`;

export const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid #DFDFDF;
    border-top: 3px solid #2563eb;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const toastIn = keyframes`
  from { opacity: 0; transform: translateY(-16px); }
  to { opacity: 1; transform: none; }
`;

export const ToastContainer = styled.div`
  position: fixed;
  top: 32px;
  right: 32px;
  z-index: 300;
`;

export const Toast = styled.div`
  background: #2563eb;
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(30,41,59,0.12);
  animation: ${toastIn} 0.3s;
  display: flex;
  align-items: center;
  gap: 16px;
`;

// --- Tooltips ---
export const Tooltip = styled.span`
  position: relative;
  display: inline-block;
  cursor: pointer;
  margin-left: 6px;
  color: #64748b;
  font-size: 15px;
  &:hover > span {
    visibility: visible;
    opacity: 1;
  }
`;

export const TooltipText = styled.span`
  visibility: hidden;
  opacity: 0;
  width: 180px;
  background: #22223b;
  color: #fff;
  text-align: left;
  border-radius: 6px;
  padding: 8px 12px;
  position: absolute;
  z-index: 10;
  bottom: 120%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 13px;
  transition: opacity 0.2s;
`;

// --- Output ---
export const JSONOutput = styled.pre`
  background: #1e293b;
  color: #f1f5f9;
  padding: 18px;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 13px;
  line-height: 1.5;
  margin-top: 32px;
`; 